import { useReducer, useState, useEffect } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import TopHeader from "../../components/common/Header/TopHeader";
import { API_BASE_URL } from "../../helpers/urlHelper";

// Estado inicial de la aplicación - Define la estructura completa del tratamiento médico
const initialState = {
  metadata: {
    templateName: "Template treatment name",
    protocol: "XY-XY",
    version: "0",
    specialty: "",
    status: "",
    purpose: "Enter the purpose of the treatment",
  },
  clinicalData: {
    ageRange: "18 - 75",
    weight: "50 - 150",
    height: "150 - 200",
    allergies: "Penicillin, Sulfonamides, NSAIDs, Latex",
    medicalHistory: {
      Hypertension: false,
      Diabetes: false,
      "Heart Diseases": false,
      Asthma: false,
      Stroke: false,
      Cancer: false,
    },
    customFields: [], // Array to store custom fields
  },
  phases: [
    {
      id: 1,
      name: "Diagnostic Phase",
      startTime: "Immediate Start - Day 0",
      duration: {
        value: 10,
        unit: "days",
      },
      responsible: "treating",
      criteria:
        "Complete all initial tests and establish baseline blood glucose values",
      steps: [
        {
          id: 1,
          name: "Initial Consultation",
          type: "Consultation",
          coordinator: "Doctor and Patient",
          relativeTime: 0,
          description:
            "Full initial evaluation of the patient, review of medical history and family history of diabetes.",
          patientInstructions:
            "Come fasting for 8 hours. Bring previous exam results if available.",
        },
        {
          id: 2,
          name: "Initial Blood Analysis",
          type: "Test",
          coordinator: "Patient Only",
          relativeTime: 1,
          description:
            "Complete blood analysis including: fasting glucose, HbA1c, lipid profile, kidney function, electrolytes, and liver enzymes.",
          documents: ["Consent.pdf"],
        },
      ],
    },
    {
      id: 2,
      name: "Therapeutic Phase",
      startTime: "Start day 14",
      duration: {
        value: 30,
        unit: "weeks",
      },
      responsible: "Endocrinologist",
      steps: [
        {
          id: 1,
          name: "Medication Start",
          type: "Medication",
          priority: "Important",
          setReminder: "Yes",
          medications: [
            {
              name: "Metformin",
              dose: 850,
              doseUnit: "mg",
              route: "Oral",
              frequency: "Every 12 hours",
              duration: {
                value: 30,
                unit: "Days",
              },
              instructions:
                "Take with meals to minimize gastrointestinal side effects. Start with a low dose and increase gradually.",
            },
          ],
        },
        {
          id: 2,
          name: "Nutritional Education",
          type: "Education",
          coordinator: "Nutritionist",
          relativeTime: 7,
          description:
            "Educational session on proper nutrition for diabetic patients, carbohydrate counting, and personalized meal planning.",
          documents: ["NutritionGuide.pdf", "MealPlanTemplate.pdf"],
        },
      ],
    },
    {
      id: 3,
      name: "Follow-up Phase",
      startTime: "Start on day 44",
      duration: {
        value: 6,
        unit: "Days",
      },
      responsible: "Treating doctor",
      criteria:
        "Stabilization of blood glucose levels within target ranges and HbA1c < 7.0%.",
      steps: [
        {
          id: 1,
          name: "Monthly Control",
          type: "Consultation",
          frequency: "Monthly",
          description:
            "Follow-up consultation to evaluate glycemic control, adverse effects, treatment adherence, and adjust medication if necessary.",
          survey: {
            diabetesControl: null,
            symptoms: {
              Dizziness: false,
              "Excessive sweating": false,
              "Blurred vision": false,
              Fatigue: false,
            },
            comments: "",
          },
        },
      ],
    },
  ],
  monitoring: {
    reminders: {
      sms: true,
      email: true,
      notificationApp: false,
      call: true,
    },
    recipients: {
      patient: true,
      doctor: false,
      family: true,
      nursing: false,
    },
    complianceThreshold: 70,
    nonComplianceActions: [
      "Phone contact with patient",
      "Reschedule appointment",
    ],
  },
  resources: [
    {
      id: 1,
      name: "Glucose Meter",
      type: "Equipment",
      responsible: "Patient",
      availability: "Continuous",
    },
    {
      id: 2,
      name: "Endocrinology Consultation",
      type: "Room",
      responsible: "Hospital",
      availability: "Scheduled",
    },
    {
      id: 3,
      name: "Nutritionist",
      type: "Medical Staff",
      responsible: "Hospital",
      availability: "On time",
    },
  ],
};

// Reducer para manejar todas las acciones que modifican el estado
function treatmentReducer(state, action) {
  switch (action.type) {
    // Acciones para metadatos - Actualiza los campos de metadatos del tratamiento
    case "UPDATE_METADATA":
      return {
        ...state,
        metadata: {
          ...state.metadata,
          [action.field]: action.value,
        },
      };

    // Acciones para datos clínicos - Actualiza los datos clínicos del paciente
    case "UPDATE_CLINICAL_DATA":
      return {
        ...state,
        clinicalData: {
          ...state.clinicalData,
          [action.field]: action.value,
        },
      };

    // Alterna el estado de las condiciones médicas en el historial médico
    case "TOGGLE_MEDICAL_HISTORY":
      return {
        ...state,
        clinicalData: {
          ...state.clinicalData,
          medicalHistory: {
            ...state.clinicalData.medicalHistory,
            [action.condition]:
              !state.clinicalData.medicalHistory[action.condition],
          },
        },
      };

    // Add custom field to clinical data
    case "ADD_CUSTOM_FIELD":
      return {
        ...state,
        clinicalData: {
          ...state.clinicalData,
          customFields: [
            ...state.clinicalData.customFields,
            {
              id: Date.now(),
              name: action.name,
              type: action.fieldType,
              value: action.fieldType === "checkbox" ? false : "",
            },
          ],
        },
      };

    // Update custom field value
    case "UPDATE_CUSTOM_FIELD":
      return {
        ...state,
        clinicalData: {
          ...state.clinicalData,
          customFields: state.clinicalData.customFields.map((field) =>
            field.id === action.fieldId
              ? { ...field, value: action.value }
              : field
          ),
        },
      };

    // Delete custom field
    case "DELETE_CUSTOM_FIELD":
      return {
        ...state,
        clinicalData: {
          ...state.clinicalData,
          customFields: state.clinicalData.customFields.filter(
            (field) => field.id !== action.fieldId
          ),
        },
      };

    // Acciones para fases - Añade una nueva fase al tratamiento
    case "ADD_PHASE":
      return {
        ...state,
        phases: [
          ...state.phases,
          {
            id: Date.now(),
            name: action.name || "New Phase",
            startTime: action.startTime || "Start day X",
            duration: action.duration || { value: 1, unit: "days" },
            responsible: action.responsible || "",
            criteria: action.criteria || "",
            steps: [],
          },
        ],
      };

    // Actualiza una fase existente
    case "UPDATE_PHASE":
      return {
        ...state,
        phases: state.phases.map((phase) =>
          phase.id === action.phaseId
            ? { ...phase, [action.field]: action.value }
            : phase
        ),
      };

    // Elimina una fase
    case "DELETE_PHASE":
      return {
        ...state,
        phases: state.phases.filter((phase) => phase.id !== action.phaseId),
      };

    // Acciones para pasos dentro de fases - Añade un nuevo paso a una fase
    case "ADD_STEP":
      return {
        ...state,
        phases: state.phases.map((phase) =>
          phase.id === action.phaseId
            ? {
                ...phase,
                steps: [
                  ...phase.steps,
                  action.step || {
                    id: Date.now(),
                    name: action.name || "New Step",
                    type: action.type || "Consultation",
                    coordinator: action.coordinator || "",
                    relativeTime: action.relativeTime || 0,
                    description: action.description || "",
                    ...(action.type === "Medication"
                      ? { medications: [] }
                      : {}),
                  },
                ],
              }
            : phase
        ),
      };

    // Actualiza un paso existente
    case "UPDATE_STEP":
      return {
        ...state,
        phases: state.phases.map((phase) =>
          phase.id === action.phaseId
            ? {
                ...phase,
                steps: phase.steps.map((step) =>
                  step.id === action.stepId
                    ? { ...step, [action.field]: action.value }
                    : step
                ),
              }
            : phase
        ),
      };

    // Elimina un paso
    case "DELETE_STEP":
      return {
        ...state,
        phases: state.phases.map((phase) =>
          phase.id === action.phaseId
            ? {
                ...phase,
                steps: phase.steps.filter((step) => step.id !== action.stepId),
              }
            : phase
        ),
      };

    // Acciones para medicamentos - Añade un nuevo medicamento a un paso
    case "ADD_MEDICATION":
      return {
        ...state,
        phases: state.phases.map((phase) =>
          phase.id === action.phaseId
            ? {
                ...phase,
                steps: phase.steps.map((step) =>
                  step.id === action.stepId
                    ? {
                        ...step,
                        medications: [
                          ...(step.medications || []),
                          {
                            name: "",
                            dose: 0,
                            doseUnit: "mg",
                            route: "Oral",
                            frequency: "Every 24 hours",
                            duration: { value: 1, unit: "Days" },
                            instructions: "",
                          },
                        ],
                      }
                    : step
                ),
              }
            : phase
        ),
      };

    // Actualiza un medicamento existente
    case "UPDATE_MEDICATION":
      return {
        ...state,
        phases: state.phases.map((phase) =>
          phase.id === action.phaseId
            ? {
                ...phase,
                steps: phase.steps.map((step) =>
                  step.id === action.stepId
                    ? {
                        ...step,
                        medications: step.medications.map((med, index) =>
                          index === action.medIndex
                            ? { ...med, [action.field]: action.value }
                            : med
                        ),
                      }
                    : step
                ),
              }
            : phase
        ),
      };

    // Elimina un medicamento
    case "DELETE_MEDICATION":
      return {
        ...state,
        phases: state.phases.map((phase) =>
          phase.id === action.phaseId
            ? {
                ...phase,
                steps: phase.steps.map((step) =>
                  step.id === action.stepId && step.medications
                    ? {
                        ...step,
                        medications: step.medications.filter(
                          (_, index) => index !== action.medIndex
                        ),
                      }
                    : step
                ),
              }
            : phase
        ),
      };

    // Acciones para monitoreo - Alterna opciones de recordatorios
    case "TOGGLE_REMINDER":
      return {
        ...state,
        monitoring: {
          ...state.monitoring,
          reminders: {
            ...state.monitoring.reminders,
            [action.option]: !state.monitoring.reminders[action.option],
          },
        },
      };

    // Alterna opciones de destinatarios
    case "TOGGLE_RECIPIENT":
      return {
        ...state,
        monitoring: {
          ...state.monitoring,
          recipients: {
            ...state.monitoring.recipients,
            [action.option]: !state.monitoring.recipients[action.option],
          },
        },
      };

    // Actualiza el umbral de cumplimiento
    case "UPDATE_COMPLIANCE_THRESHOLD":
      return {
        ...state,
        monitoring: {
          ...state.monitoring,
          complianceThreshold: action.value,
        },
      };

    // Actualiza las acciones de no cumplimiento
    case "UPDATE_NON_COMPLIANCE_ACTIONS":
      return {
        ...state,
        monitoring: {
          ...state.monitoring,
          nonComplianceActions: action.actions,
        },
      };

    // Acciones para recursos - Establece todos los recursos
    case "SET_RESOURCES":
      return {
        ...state,
        resources: action.resources,
      };

    // Añade un nuevo recurso
    case "ADD_RESOURCE":
      return {
        ...state,
        resources: [...state.resources, action.resource],
      };

    // Actualiza un recurso existente
    case "UPDATE_RESOURCE":
      return {
        ...state,
        resources: state.resources.map((resource) =>
          resource.id === action.resourceId
            ? { ...resource, [action.field]: action.value }
            : resource
        ),
      };

    // Elimina un recurso
    case "DELETE_RESOURCE":
      return {
        ...state,
        resources: state.resources.filter(
          (resource) => resource.id !== action.resourceId
        ),
      };

    case "SET_FULL_STATE":
      return action.state;

    default:
      return state;
  }
}

// Componente Modal - Reutilizable para mostrar diferentes tipos de modales
function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = "md",
  footer = null,
}) {
  if (!isOpen) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className={`modal-dialog modal-${size} modal-dialog-centered`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

// Componente de confirmación - Para acciones que requieren confirmación del usuario
function ConfirmationModal({ isOpen, onClose, onConfirm, title, message }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={onConfirm}>
            Confirm
          </button>
        </>
      }
    >
      <p>{message}</p>
    </Modal>
  );
}

// Componente principal de tratamiento
function Treatment({ initialData, treatmentId }) {
  // Estado principal usando el reducer
  const [state, dispatch] = useReducer(
    treatmentReducer,
    initialData || initialState
  );

  const location = useLocation();
  const navigate = useNavigate();
  const treatmentData = location.state?.treatmentData;

  // Initialize state with treatment data if available
  useEffect(() => {
    if (treatmentData) {
      // Map the API data structure to the component's state structure
      const mappedState = {
        metadata: {
          templateName:
            treatmentData.template?.name || "Template treatment name",
          protocol: treatmentData.template?.protocol_code || "XY-XY",
          version: treatmentData.template?.version?.toString() || "0",
          specialty: treatmentData.template?.specialty || "",
          status: treatmentData.template?.status || "",
          purpose:
            treatmentData.template?.purpose ||
            "Enter the purpose of the treatment",
        },
        clinicalData: {
          ageRange: `${treatmentData.clinical_data?.min_age || 18} - ${
            treatmentData.clinical_data?.max_age || 75
          }`,
          weight: `${treatmentData.clinical_data?.min_weight || 50} - ${
            treatmentData.clinical_data?.max_weight || 150
          }`,
          height: `${treatmentData.clinical_data?.min_height || 150} - ${
            treatmentData.clinical_data?.max_height || 200
          }`,
          allergies:
            treatmentData.patient?.allergies?.join(", ") ||
            "Penicillin, Sulfonamides, NSAIDs, Latex",
          medicalHistory: {
            ...state.clinicalData.medicalHistory,
            // Map medical history from the API to the component state
            ...(treatmentData.patient?.medical_history?.reduce(
              (acc, condition) => {
                acc[condition] = true;
                return acc;
              },
              {}
            ) || {}),
          },
          customFields: state.clinicalData.customFields,
        },
        phases:
          treatmentData.phases?.map((phase) => ({
            id: phase.order || Date.now(),
            name: phase.name || "New Phase",
            startTime: `Start day ${phase.start_days || 0}`,
            duration: {
              value: phase.estimated_duration_days || 1,
              unit: "days",
            },
            responsible: phase.responsible || "",
            criteria: phase.end_conditions || "",
            steps: [], // You would need to map steps if available in your API
          })) || state.phases,
        monitoring: state.monitoring,
        resources: state.resources,
      };

      // Update the state with the mapped data
      dispatch({ type: "SET_FULL_STATE", state: mappedState });
    }
  }, [treatmentData]);

  // Estados para controlar la visibilidad de los modales
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [showEditPhaseModal, setShowEditPhaseModal] = useState(false);
  const [showDeletePhaseModal, setShowDeletePhaseModal] = useState(false);
  const [showEditStepModal, setShowEditStepModal] = useState(false);
  const [showDeleteStepModal, setShowDeleteStepModal] = useState(false);
  const [showDeleteMedicationModal, setShowDeleteMedicationModal] =
    useState(false);
  const [showAddResourceModal, setShowAddResourceModal] = useState(false);
  const [showEditResourceModal, setShowEditResourceModal] = useState(false);
  const [showDeleteResourceModal, setShowDeleteResourceModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);

  // Estados para almacenar elementos seleccionados y datos de formularios
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [newFieldData, setNewFieldData] = useState({ name: "", type: "text" });
  const [newPhaseData, setNewPhaseData] = useState({
    name: "",
    startTime: "",
    duration: { value: 1, unit: "days" },
    responsible: "",
    criteria: "",
  });
  const [newStepData, setNewStepData] = useState({
    name: "",
    type: "Consultation",
    coordinator: "",
    relativeTime: 0,
    description: "",
  });
  const [newResourceData, setNewResourceData] = useState({
    name: "",
    type: "",
    responsible: "",
    availability: "",
  });

  // Efecto para verificar los recursos cuando cambian
  useEffect(() => {
    console.log("Current resources:", state.resources);
  }, [state.resources]);

  // Manejadores para metadatos y datos clínicos
  const handleMetadataChange = (field, value) => {
    dispatch({ type: "UPDATE_METADATA", field, value });
  };

  const handleClinicalDataChange = (field, value) => {
    dispatch({ type: "UPDATE_CLINICAL_DATA", field, value });
  };

  const handleMedicalHistoryToggle = (condition) => {
    dispatch({ type: "TOGGLE_MEDICAL_HISTORY", condition });
  };

  // Handlers for custom fields
  const handleAddCustomField = () => {
    if (!newFieldData.name) {
      alert("Please enter a field name");
      return;
    }

    dispatch({
      type: "ADD_CUSTOM_FIELD",
      name: newFieldData.name,
      fieldType: newFieldData.type,
    });

    setShowAddFieldModal(false);
    setNewFieldData({ name: "", type: "text" });
  };

  const handleCustomFieldChange = (fieldId, value) => {
    dispatch({
      type: "UPDATE_CUSTOM_FIELD",
      fieldId,
      value,
    });
  };

  const handleDeleteCustomField = (fieldId) => {
    dispatch({
      type: "DELETE_CUSTOM_FIELD",
      fieldId,
    });
  };

  // Manejadores para fases
  const handlePhaseChange = (phaseId, field, value) => {
    dispatch({ type: "UPDATE_PHASE", phaseId, field, value });
  };

  const handleAddPhase = () => {
    dispatch({
      type: "ADD_PHASE",
      name: newPhaseData.name,
      startTime: newPhaseData.startTime,
      duration: newPhaseData.duration,
      responsible: newPhaseData.responsible,
      criteria: newPhaseData.criteria,
    });
    setNewPhaseData({
      name: "",
      startTime: "",
      duration: { value: 1, unit: "days" },
      responsible: "",
      criteria: "",
    });
  };

  const handleDeletePhase = (phaseId) => {
    setSelectedPhase(phaseId);
    setShowDeletePhaseModal(true);
  };

  const confirmDeletePhase = () => {
    dispatch({ type: "DELETE_PHASE", phaseId: selectedPhase });
    setShowDeletePhaseModal(false);
  };

  const handleEditPhase = (phase) => {
    setSelectedPhase(phase.id);
    setNewPhaseData({
      name: phase.name,
      startTime: phase.startTime,
      duration: phase.duration,
      responsible: phase.responsible,
      criteria: phase.criteria || "",
    });
    setShowEditPhaseModal(true);
  };

  const saveEditedPhase = () => {
    handlePhaseChange(selectedPhase, "name", newPhaseData.name);
    handlePhaseChange(selectedPhase, "startTime", newPhaseData.startTime);
    handlePhaseChange(selectedPhase, "duration", newPhaseData.duration);
    handlePhaseChange(selectedPhase, "responsible", newPhaseData.responsible);
    handlePhaseChange(selectedPhase, "criteria", newPhaseData.criteria);
    setShowEditPhaseModal(false);
  };

  // Manejadores para pasos
  const handleAddStep = (phaseId) => {
    setSelectedPhase(phaseId);
    setSelectedStep(null);
    setNewStepData({
      name: "",
      type: "Consultation",
      coordinator: "",
      relativeTime: 0,
      description: "",
    });
    setShowEditStepModal(true);
  };

  const saveNewStep = () => {
    if (!newStepData.name) {
      alert("Please enter a step name");
      return;
    }

    // Crear un objeto de paso completo con todos los campos requeridos según el tipo
    let stepData = {
      id: Date.now(), // Asegurar ID único
      name: newStepData.name,
      type: newStepData.type,
      coordinator: newStepData.coordinator || "",
      relativeTime: Number.parseInt(newStepData.relativeTime) || 0,
      description: newStepData.description || "",
    };

    // Añadir propiedades específicas según el tipo
    if (newStepData.type === "Medication") {
      stepData = {
        ...stepData,
        medications: [],
        priority: "Normal",
        setReminder: "No",
      };
    } else if (newStepData.type === "Consultation") {
      stepData = {
        ...stepData,
        frequency: "Once",
      };
    }

    // Dispatch con el objeto de paso completo
    dispatch({
      type: "ADD_STEP",
      phaseId: selectedPhase,
      step: stepData,
    });

    console.log("Added step to phase:", selectedPhase, stepData);
    setShowEditStepModal(false);
  };

  const handleStepChange = (phaseId, stepId, field, value) => {
    dispatch({ type: "UPDATE_STEP", phaseId, stepId, field, value });
  };

  const handleEditStep = (phaseId, step) => {
    setSelectedPhase(phaseId);
    setSelectedStep(step.id);
    setNewStepData({
      name: step.name,
      type: step.type,
      coordinator: step.coordinator || "",
      relativeTime: step.relativeTime || 0,
      description: step.description || "",
    });
    setShowEditStepModal(true);
  };

  const saveEditedStep = () => {
    handleStepChange(selectedPhase, selectedStep, "name", newStepData.name);
    handleStepChange(selectedPhase, selectedStep, "type", newStepData.type);
    handleStepChange(
      selectedPhase,
      selectedStep,
      "coordinator",
      newStepData.coordinator
    );
    handleStepChange(
      selectedPhase,
      selectedStep,
      "relativeTime",
      newStepData.relativeTime
    );
    handleStepChange(
      selectedPhase,
      selectedStep,
      "description",
      newStepData.description
    );
    setShowEditStepModal(false);
  };

  const handleDeleteStep = (phaseId, stepId) => {
    setSelectedPhase(phaseId);
    setSelectedStep(stepId);
    setShowDeleteStepModal(true);
  };

  const confirmDeleteStep = () => {
    dispatch({
      type: "DELETE_STEP",
      phaseId: selectedPhase,
      stepId: selectedStep,
    });
    setShowDeleteStepModal(false);
  };

  // Manejadores para medicamentos
  const handleAddMedication = (phaseId, stepId) => {
    dispatch({ type: "ADD_MEDICATION", phaseId, stepId });
  };

  const handleMedicationChange = (phaseId, stepId, medIndex, field, value) => {
    dispatch({
      type: "UPDATE_MEDICATION",
      phaseId,
      stepId,
      medIndex,
      field,
      value,
    });
  };

  const handleDeleteMedication = (phaseId, stepId, medIndex) => {
    setSelectedPhase(phaseId);
    setSelectedStep(stepId);
    setSelectedMedication(medIndex);
    setShowDeleteMedicationModal(true);
  };

  const confirmDeleteMedication = () => {
    dispatch({
      type: "DELETE_MEDICATION",
      phaseId: selectedPhase,
      stepId: selectedStep,
      medIndex: selectedMedication,
    });
    setShowDeleteMedicationModal(false);
  };

  // Manejadores para monitoreo
  const handleReminderToggle = (option) => {
    dispatch({ type: "TOGGLE_REMINDER", option });
  };

  const handleRecipientToggle = (option) => {
    dispatch({ type: "TOGGLE_RECIPIENT", option });
  };

  const handleComplianceThresholdChange = (value) => {
    dispatch({ type: "UPDATE_COMPLIANCE_THRESHOLD", value });
  };

  const handleNonComplianceActionsChange = (e) => {
    const options = e.target.options;
    const selectedValues = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selectedValues.push(options[i].value);
      }
    }
    dispatch({
      type: "UPDATE_NON_COMPLIANCE_ACTIONS",
      actions: selectedValues,
    });
  };

  // Manejadores para recursos
  const handleAddResource = () => {
    // Limpiar el formulario de recursos
    setNewResourceData({
      name: "",
      type: "",
      responsible: "",
      availability: "",
    });
    // Mostrar el modal
    setShowAddResourceModal(true);
  };

  const saveNewResource = () => {
    // Validación básica
    if (!newResourceData.name || !newResourceData.type) {
      alert("Please enter a name and type for the resource");
      return;
    }

    // Crear un nuevo recurso con ID único
    const newResource = {
      id: Date.now(),
      name: newResourceData.name,
      type: newResourceData.type,
      responsible: newResourceData.responsible || "",
      availability: newResourceData.availability || "",
    };

    // Añadir directamente al estado
    dispatch({
      type: "ADD_RESOURCE",
      resource: newResource,
    });

    console.log("New resource added:", newResource);

    // Cerrar el modal y limpiar el formulario
    setShowAddResourceModal(false);
    setNewResourceData({
      name: "",
      type: "",
      responsible: "",
      availability: "",
    });
  };

  const handleEditResource = (resource) => {
    setSelectedResource(resource.id);
    setNewResourceData({
      name: resource.name,
      type: resource.type,
      responsible: resource.responsible || "",
      availability: resource.availability || "",
    });
    setShowEditResourceModal(true);
  };

  const saveEditedResource = () => {
    // Actualizar cada campo del recurso
    const updatedResource = {
      id: selectedResource,
      name: newResourceData.name,
      type: newResourceData.type,
      responsible: newResourceData.responsible || "",
      availability: newResourceData.availability || "",
    };

    // Actualizar todos los recursos
    const updatedResources = state.resources.map((resource) =>
      resource.id === selectedResource ? updatedResource : resource
    );

    // Actualizar el estado completo
    dispatch({
      type: "SET_RESOURCES",
      resources: updatedResources,
    });

    setShowEditResourceModal(false);
  };

  const handleDeleteResource = (resourceId) => {
    setSelectedResource(resourceId);
    setShowDeleteResourceModal(true);
  };

  const confirmDeleteResource = () => {
    dispatch({ type: "DELETE_RESOURCE", resourceId: selectedResource });
    setShowDeleteResourceModal(false);
  };

  // Manejadores para acciones generales
  const handleSave = () => {
    setShowSaveModal(true);
  };

  const confirmSave = async () => {
    try {
      // Create the request body according to the specified structure
      const requestBody = {
        template: {
          name: state.metadata.templateName,
          protocol_code: state.metadata.protocol,
          version: Number.parseInt(state.metadata.version) || 1,
          authors: ["Dr. Juan Pérez", "Dr. Ana Gómez"], // Default authors
          creation_date:
            treatmentData?.template?.creation_date || new Date().toISOString(),
          last_modified: new Date().toISOString(),
          specialty_id:
            treatmentData?.template?.specialty_id || "60b75f8e4d1d2a58b6f9b0d3", // Default ID
          purpose: state.metadata.purpose,
          status: state.metadata.status.toLowerCase() || "draft",
          keywords: ["hipertensión", "presión arterial"], // Default keywords
          scientific_evidence:
            "Estudios clínicos demuestran la efectividad de este tratamiento.",
        },
        clinical_data: {
          min_age:
            Number.parseInt(state.clinicalData.ageRange.split("-")[0]) || 30,
          max_age:
            Number.parseInt(state.clinicalData.ageRange.split("-")[1]) || 70,
          min_weight:
            Number.parseInt(state.clinicalData.weight.split("-")[0]) || 45,
          max_weight:
            Number.parseInt(state.clinicalData.weight.split("-")[1]) || 120,
          min_height:
            Number.parseInt(state.clinicalData.height.split("-")[0]) || 150,
          max_height:
            Number.parseInt(state.clinicalData.height.split("-")[1]) || 200,
          min_bmi: 18.5,
          max_bmi: 30,
          applicable_gender: "M",
        },
        phases: state.phases.map((phase) => ({
          name: phase.name,
          order: phase.id,
          type: "diagnostic",
          start_days: 0,
          estimated_duration_days:
            phase.duration.unit === "days"
              ? phase.duration.value
              : phase.duration.value * 7,
          responsible: phase.responsible,
          start_conditions: "Paciente diagnosticado con hipertensión",
          end_conditions: phase.criteria || "Presión arterial estabilizada",
          description:
            phase.steps[0]?.description || "Control inicial de la hipertensión",
          objectives: "Bajar la presión arterial a niveles normales",
          progress_indicators: ["Presión arterial", "Peso"],
          generates_alerts: true,
        })),
        patient: {
          first_name: "Carlos",
          last_name: "González",
          birth_date: "1985-04-22",
          identification: "1234567890",
          gender: "M",
          address: "Calle Ficticia 123, Ciudad",
          phone: "555-1234567",
          email: "carlos.gonzalez@example.com",
          allergies: state.clinicalData.allergies
            .split(",")
            .map((a) => a.trim()),
          medical_history: Object.keys(
            state.clinicalData.medicalHistory
          ).filter((key) => state.clinicalData.medicalHistory[key]),
        },
        treatment_assigned: {
          template_id:
            treatmentData?.treatment_assigned?.template_id ||
            "60b75f8e4d1d2a58b6f9b0d3",
          patient_id:
            treatmentData?.treatment_assigned?.patient_id ||
            "60b75f8e4d1d2a58b6f9b0d4",
          doctor_id:
            treatmentData?.treatment_assigned?.doctor_id ||
            "60b75f8e4d1d2a58b6f9b0d5",
          start_date:
            treatmentData?.treatment_assigned?.start_date ||
            new Date().toISOString(),
          estimated_end_date:
            treatmentData?.treatment_assigned?.estimated_end_date ||
            new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          status: "pending",
          initial_notes: "Paciente en estado estable.",
          customizations: {},
        },
        treatment_progress: {
          percentage_completed:
            treatmentData?.treatment_progress?.percentage_completed || 0,
          last_follow_up:
            treatmentData?.treatment_progress?.last_follow_up ||
            new Date().toISOString(),
          on_time: true,
          deviations: null,
          general_notes: "Tratamiento en progreso",
        },
      };

      // Make the POST request - use PUT if updating an existing treatment
      const url = treatmentData
        ? `${API_BASE_URL}/treatmentManagement/${treatmentData._id}`
        : `${API_BASE_URL}/treatmentManagement`;

      const method = treatmentData ? "put" : "post";
      const response = await axios[method](url, requestBody);

      console.log("Treatment saved successfully:", response.data);
      setShowSaveModal(false);
      alert("Treatment saved successfully");

      // Navigate back to the treatments list
      if (navigate) {
         navigate("/treatmentList");
      }
    } catch (error) {
      console.error("Error saving treatment:", error);
      alert(`Error saving treatment: ${error.message}`);
    }
  };

  const handlePreview = () => {
    setShowPreviewModal(true);
  };

  const handleCancel = () => {
    setShowCancelModal(true);
  };

  const confirmCancel = () => {
    setShowCancelModal(false);
    alert("Operation cancelled");
    navigate("/treatments");
  };

  const handlePublish = () => {
    setShowPublishModal(true);
  };

  const confirmPublish = () => {
    console.log("Publishing treatment:", state);
    setShowPublishModal(false);
    // Aquí iría la lógica para publicar el tratamiento
    alert("Treatment published successfully");
  };

  return (

    <main id="app">
            <TopHeader />
      <div className="container my-4">
        <div className="container my-4">
          {/* Encabezado y botones principales */}
          <div className="d-flex justify-content-between">
            <div className="d-flex align-items-center">
              {navigate && (
                <button
                  className="btn btn-outline-secondary me-3"
                  onClick={() => navigate("/treatmentTemplate")}
                >
                  &larr; Back
                </button>
              )}
              <h2 className="mb-0 fw-bold">
                {treatmentId && treatmentId !== "new"
                  ? "Edit Treatment"
                  : "New Treatment"}
              </h2>
            </div>
            <div className="text-end mb-3">
              <button className="btn btn-success me-2" onClick={handleSave}>
                Save
              </button>
              <button className="btn btn-primary" onClick={handlePreview}>
                Preview
              </button>
            </div>
          </div>

          {/* Sección de metadatos */}
          <div className="card mb-4">
            <h5 className="card-header">Template Metadata</h5>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Template Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={state.metadata.templateName}
                    onChange={(e) =>
                      handleMetadataChange("templateName", e.target.value)
                    }
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Treatment Protocol</label>
                  <input
                    type="text"
                    className="form-control"
                    value={state.metadata.protocol}
                    onChange={(e) =>
                      handleMetadataChange("protocol", e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Version</label>
                  <input
                    type="text"
                    className="form-control"
                    value={state.metadata.version}
                    onChange={(e) =>
                      handleMetadataChange("version", e.target.value)
                    }
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Specialty</label>
                  <select
                    className="form-select"
                    value={state.metadata.specialty}
                    onChange={(e) =>
                      handleMetadataChange("specialty", e.target.value)
                    }
                  >
                    <option value="" disabled>
                      Select a specialty
                    </option>
                    <option>Cardiology</option>
                    <option>Neurology</option>
                    <option>Pediatrics</option>
                    <option>Oncology</option>
                  </select>
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={state.metadata.status}
                    onChange={(e) =>
                      handleMetadataChange("status", e.target.value)
                    }
                  >
                    <option value="" disabled>
                      Select a status
                    </option>
                    <option>Draft</option>
                    <option>Under review</option>
                    <option>Approved</option>
                    <option>Archived</option>
                  </select>
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Purpose</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={state.metadata.purpose}
                  onChange={(e) =>
                    handleMetadataChange("purpose", e.target.value)
                  }
                ></textarea>
              </div>
            </div>
          </div>

          {/* Sección de datos clínicos */}
          <div className="card mb-4">
            <h5 className="card-header d-flex justify-content-between align-items-center">
              Basic Clinical Data{" "}
              <button
                className="btn btn-primary"
                onClick={() => setShowAddFieldModal(true)}
              >
                + Add Field
              </button>
            </h5>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Age (range)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={state.clinicalData.ageRange}
                    onChange={(e) =>
                      handleClinicalDataChange("ageRange", e.target.value)
                    }
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Weight (kg)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={state.clinicalData.weight}
                    onChange={(e) =>
                      handleClinicalDataChange("weight", e.target.value)
                    }
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Height (cm)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={state.clinicalData.height}
                    onChange={(e) =>
                      handleClinicalDataChange("height", e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Known Allergies</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={state.clinicalData.allergies}
                  onChange={(e) =>
                    handleClinicalDataChange("allergies", e.target.value)
                  }
                ></textarea>
              </div>
              <div className="mb-3">
                <label className="form-label">Relevant Medical History</label>
                <div className="d-flex gap-3 flex-wrap">
                  {Object.keys(state.clinicalData.medicalHistory).map(
                    (condition, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center gap-1"
                      >
                        <input
                          type="checkbox"
                          id={`condition${index + 1}`}
                          className="form-check-input"
                          checked={state.clinicalData.medicalHistory[condition]}
                          onChange={() => handleMedicalHistoryToggle(condition)}
                        />
                        <label
                          htmlFor={`condition${index + 1}`}
                          className="mb-0"
                        >
                          {condition}
                        </label>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Custom Fields Section */}
              {state.clinicalData.customFields.length > 0 && (
                <div className="mt-3">
                  <label className="form-label">Custom Fields</label>
                  <div className="row">
                    {state.clinicalData.customFields.map((field) => (
                      <div key={field.id} className="col-md-6 mb-3">
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1">
                            <label className="form-label">{field.name}</label>
                            {field.type === "text" && (
                              <input
                                type="text"
                                className="form-control"
                                value={field.value}
                                onChange={(e) =>
                                  handleCustomFieldChange(
                                    field.id,
                                    e.target.value
                                  )
                                }
                              />
                            )}
                            {field.type === "number" && (
                              <input
                                type="number"
                                className="form-control"
                                value={field.value}
                                onChange={(e) =>
                                  handleCustomFieldChange(
                                    field.id,
                                    e.target.value
                                  )
                                }
                              />
                            )}
                            {field.type === "date" && (
                              <input
                                type="date"
                                className="form-control"
                                value={field.value}
                                onChange={(e) =>
                                  handleCustomFieldChange(
                                    field.id,
                                    e.target.value
                                  )
                                }
                              />
                            )}
                            {field.type === "select" && (
                              <select
                                className="form-select"
                                value={field.value}
                                onChange={(e) =>
                                  handleCustomFieldChange(
                                    field.id,
                                    e.target.value
                                  )
                                }
                              >
                                <option value="">Select an option</option>
                                <option value="option1">Option 1</option>
                                <option value="option2">Option 2</option>
                                <option value="option3">Option 3</option>
                              </select>
                            )}
                            {field.type === "checkbox" && (
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`custom-field-${field.id}`}
                                  checked={field.value}
                                  onChange={(e) =>
                                    handleCustomFieldChange(
                                      field.id,
                                      e.target.checked
                                    )
                                  }
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`custom-field-${field.id}`}
                                >
                                  {field.name}
                                </label>
                              </div>
                            )}
                          </div>
                          <button
                            className="btn btn-sm btn-danger ms-2"
                            onClick={() => handleDeleteCustomField(field.id)}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Encabezado de fases de tratamiento */}
          <div className="d-flex justify-content-between">
            <h2 className="mb-4 fw-bold">Treatment Phases</h2>
            <div className="text-end mb-3">
              <button
                className="btn btn-primary"
                onClick={() => {
                  setNewPhaseData({
                    name: "New Phase",
                    startTime: "Start day X",
                    duration: { value: 1, unit: "days" },
                    responsible: "",
                    criteria: "",
                  });
                  setShowEditPhaseModal(true);
                }}
              >
                + Add Phase
              </button>
            </div>
          </div>

          {/* Lista de fases */}
          {state.phases.map((phase, phaseIndex) => (
            <div className="card mb-4" key={phase.id}>
              <h5 className="card-header d-flex justify-content-between align-items-center bg-primary text-white">
                {phase.name} ({phase.startTime})
                <div>
                  <button
                    className="btn btn-light me-2"
                    onClick={() => handleEditPhase(phase)}
                  >
                    ✏️
                  </button>
                  <button
                    className="btn btn-light"
                    onClick={() => handleDeletePhase(phase.id)}
                  >
                    🗑️
                  </button>
                </div>
              </h5>
              <div className="card-body">
                {/* Información general de la fase */}
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Estimated Duration</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="number"
                        className="form-control w-25"
                        value={phase.duration.value}
                        onChange={(e) =>
                          handlePhaseChange(phase.id, "duration", {
                            ...phase.duration,
                            value: Number.parseInt(e.target.value),
                          })
                        }
                      />
                      <select
                        className="form-select w-50"
                        value={phase.duration.unit}
                        onChange={(e) =>
                          handlePhaseChange(phase.id, "duration", {
                            ...phase.duration,
                            unit: e.target.value,
                          })
                        }
                      >
                        <option value="days">Days</option>
                        <option value="weeks">Weeks</option>
                        <option value="months">Months</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Responsible</label>
                    <select
                      className="form-select"
                      value={phase.responsible}
                      onChange={(e) =>
                        handlePhaseChange(
                          phase.id,
                          "responsible",
                          e.target.value
                        )
                      }
                    >
                      <option value="treating">Treating doctor</option>
                      <option value="endocrinologist">Endocrinologist</option>
                      <option value="nurse">Nurse</option>
                    </select>
                  </div>
                </div>

                {phase.criteria && (
                  <div className="mb-3">
                    <label className="form-label">
                      Criteria for Finalizing
                    </label>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={phase.criteria}
                      onChange={(e) =>
                        handlePhaseChange(phase.id, "criteria", e.target.value)
                      }
                    ></textarea>
                  </div>
                )}

                {/* Sección de pasos */}
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4>Steps</h4>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleAddStep(phase.id)}
                    >
                      + Add Step
                    </button>
                  </div>

                  {/* Lista de pasos */}
                  {phase.steps.map((step, stepIndex) => (
                    <div className="card mb-3" key={step.id}>
                      <div className="card-header bg-info d-flex justify-content-between align-items-center">
                        <div>
                          Step {stepIndex + 1}: {step.name}
                        </div>
                        <div>
                          <button
                            className="btn btn-light me-2"
                            onClick={() => handleEditStep(phase.id, step)}
                          >
                            ✏️
                          </button>
                          <button
                            className="btn btn-light"
                            onClick={() => handleDeleteStep(phase.id, step.id)}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="row mb-3">
                          <div className="col-md-4">
                            <label className="form-label">
                              Type of Intervention
                            </label>
                            <select
                              className="form-select"
                              value={step.type}
                              onChange={(e) =>
                                handleStepChange(
                                  phase.id,
                                  step.id,
                                  "type",
                                  e.target.value
                                )
                              }
                            >
                              <option>Consultation</option>
                              <option>Medication</option>
                              <option>Test</option>
                              <option>Education</option>
                            </select>
                          </div>

                          {step.coordinator && (
                            <div className="col-md-4">
                              <label className="form-label">Coordinator</label>
                              <select
                                className="form-select"
                                value={step.coordinator}
                                onChange={(e) =>
                                  handleStepChange(
                                    phase.id,
                                    step.id,
                                    "coordinator",
                                    e.target.value
                                  )
                                }
                              >
                                <option>Doctor and Patient</option>
                                <option>Doctor Only</option>
                                <option>Patient Only</option>
                                <option>Nursing</option>
                                <option>Nutritionist</option>
                              </select>
                            </div>
                          )}

                          {step.relativeTime !== undefined && (
                            <div className="col-md-4">
                              <label className="form-label">
                                Relative Time
                              </label>
                              <div className="d-flex align-items-center gap-2">
                                <input
                                  type="number"
                                  className="form-control"
                                  style={{ width: "80px" }}
                                  value={step.relativeTime}
                                  onChange={(e) =>
                                    handleStepChange(
                                      phase.id,
                                      step.id,
                                      "relativeTime",
                                      Number.parseInt(e.target.value)
                                    )
                                  }
                                />
                                <span>Days</span>
                              </div>
                            </div>
                          )}

                          {step.priority && (
                            <div className="col-md-4">
                              <label className="form-label">Priority</label>
                              <select
                                className="form-select"
                                value={step.priority}
                                onChange={(e) =>
                                  handleStepChange(
                                    phase.id,
                                    step.id,
                                    "priority",
                                    e.target.value
                                  )
                                }
                              >
                                <option>Normal</option>
                                <option>Important</option>
                                <option>Critical</option>
                              </select>
                            </div>
                          )}

                          {step.setReminder && (
                            <div className="col-md-4">
                              <label className="form-label">Set Reminder</label>
                              <select
                                className="form-select"
                                value={step.setReminder}
                                onChange={(e) =>
                                  handleStepChange(
                                    phase.id,
                                    step.id,
                                    "setReminder",
                                    e.target.value
                                  )
                                }
                              >
                                <option>No</option>
                                <option>Yes</option>
                              </select>
                            </div>
                          )}

                          {step.frequency && (
                            <div className="col-md-4">
                              <label className="form-label">Frequency</label>
                              <select
                                className="form-select"
                                value={step.frequency}
                                onChange={(e) =>
                                  handleStepChange(
                                    phase.id,
                                    step.id,
                                    "frequency",
                                    e.target.value
                                  )
                                }
                              >
                                <option>Once</option>
                                <option>Daily</option>
                                <option>Weekly</option>
                                <option>Monthly</option>
                              </select>
                            </div>
                          )}
                        </div>

                        {step.description && (
                          <div className="mb-3">
                            <label className="form-label">
                              Detailed Description
                            </label>
                            <textarea
                              className="form-control"
                              rows="2"
                              value={step.description}
                              onChange={(e) =>
                                handleStepChange(
                                  phase.id,
                                  step.id,
                                  "description",
                                  e.target.value
                                )
                              }
                            ></textarea>
                          </div>
                        )}

                        {step.patientInstructions && (
                          <div className="mb-3">
                            <label className="form-label">
                              Instructions for Patient
                            </label>
                            <textarea
                              className="form-control"
                              rows="2"
                              value={step.patientInstructions}
                              onChange={(e) =>
                                handleStepChange(
                                  phase.id,
                                  step.id,
                                  "patientInstructions",
                                  e.target.value
                                )
                              }
                            ></textarea>
                          </div>
                        )}

                        {/* Sección de medicamentos */}
                        {step.type === "Medication" && step.medications && (
                          <div className="card mt-3">
                            <div className="card-header d-flex justify-content-between align-items-center">
                              Associated Medication
                              <button
                                className="btn btn-primary"
                                onClick={() =>
                                  handleAddMedication(phase.id, step.id)
                                }
                              >
                                + Add Medication
                              </button>
                            </div>
                            <div className="card-body">
                              {step.medications.map((medication, medIndex) => (
                                <div key={medIndex}>
                                  {medIndex > 0 && <hr className="my-3" />}
                                  <div className="d-flex justify-content-between mb-2">
                                    <h6>Medication {medIndex + 1}</h6>
                                    <button
                                      className="btn btn-sm btn-danger"
                                      onClick={() =>
                                        handleDeleteMedication(
                                          phase.id,
                                          step.id,
                                          medIndex
                                        )
                                      }
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  <div className="row mb-3">
                                    <div className="col-md-4">
                                      <label className="form-label">Name</label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={medication.name}
                                        onChange={(e) =>
                                          handleMedicationChange(
                                            phase.id,
                                            step.id,
                                            medIndex,
                                            "name",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </div>
                                    <div className="col-md-4">
                                      <label className="form-label">Dose</label>
                                      <div className="d-flex align-items-center gap-2">
                                        <input
                                          type="number"
                                          className="form-control"
                                          style={{ width: "80px" }}
                                          value={medication.dose}
                                          onChange={(e) =>
                                            handleMedicationChange(
                                              phase.id,
                                              step.id,
                                              medIndex,
                                              "dose",
                                              Number.parseInt(e.target.value)
                                            )
                                          }
                                        />
                                        <select
                                          className="form-select"
                                          style={{ width: "80px" }}
                                          value={medication.doseUnit}
                                          onChange={(e) =>
                                            handleMedicationChange(
                                              phase.id,
                                              step.id,
                                              medIndex,
                                              "doseUnit",
                                              e.target.value
                                            )
                                          }
                                        >
                                          <option>mg</option>
                                          <option>ml</option>
                                          <option>g</option>
                                        </select>
                                      </div>
                                    </div>
                                    <div className="col-md-4">
                                      <label className="form-label">
                                        Route
                                      </label>
                                      <select
                                        className="form-select"
                                        value={medication.route}
                                        onChange={(e) =>
                                          handleMedicationChange(
                                            phase.id,
                                            step.id,
                                            medIndex,
                                            "route",
                                            e.target.value
                                          )
                                        }
                                      >
                                        <option>Oral</option>
                                        <option>IV</option>
                                        <option>IM</option>
                                        <option>Topical</option>
                                      </select>
                                    </div>
                                  </div>
                                  <div className="row mb-3">
                                    <div className="col-md-6">
                                      <label className="form-label">
                                        Frequency
                                      </label>
                                      <select
                                        className="form-select"
                                        value={medication.frequency}
                                        onChange={(e) =>
                                          handleMedicationChange(
                                            phase.id,
                                            step.id,
                                            medIndex,
                                            "frequency",
                                            e.target.value
                                          )
                                        }
                                      >
                                        <option>Every 4 hours</option>
                                        <option>Every 6 hours</option>
                                        <option>Every 8 hours</option>
                                        <option>Every 12 hours</option>
                                        <option>Every 24 hours</option>
                                      </select>
                                    </div>
                                    <div className="col-md-6">
                                      <label className="form-label">
                                        Duration
                                      </label>
                                      <div className="d-flex align-items-center gap-2">
                                        <input
                                          type="number"
                                          className="form-control"
                                          style={{ width: "60px" }}
                                          value={medication.duration.value}
                                          onChange={(e) =>
                                            handleMedicationChange(
                                              phase.id,
                                              step.id,
                                              medIndex,
                                              "duration",
                                              {
                                                ...medication.duration,
                                                value: Number.parseInt(
                                                  e.target.value
                                                ),
                                              }
                                            )
                                          }
                                        />
                                        <select
                                          className="form-select"
                                          style={{ width: "100px" }}
                                          value={medication.duration.unit}
                                          onChange={(e) =>
                                            handleMedicationChange(
                                              phase.id,
                                              step.id,
                                              medIndex,
                                              "duration",
                                              {
                                                ...medication.duration,
                                                unit: e.target.value,
                                              }
                                            )
                                          }
                                        >
                                          <option>Hours</option>
                                          <option>Days</option>
                                          <option>Weeks</option>
                                        </select>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="mb-3">
                                    <label className="form-label">
                                      Specific Instructions
                                    </label>
                                    <textarea
                                      className="form-control"
                                      rows="2"
                                      value={medication.instructions}
                                      onChange={(e) =>
                                        handleMedicationChange(
                                          phase.id,
                                          step.id,
                                          medIndex,
                                          "instructions",
                                          e.target.value
                                        )
                                      }
                                    ></textarea>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Sección de encuesta */}
                        {step.survey && (
                          <div className="mb-3">
                            <label className="form-label">
                              Feedback Survey
                            </label>
                            <div className="border p-3 rounded">
                              <div className="mb-3">
                                <label className="form-label">
                                  How would you rate your diabetes control?
                                </label>
                                <div className="d-flex gap-3 flex-wrap">
                                  {[1, 2, 3, 4, 5].map((num) => (
                                    <div
                                      key={num}
                                      className="d-flex align-items-center gap-1"
                                    >
                                      <input
                                        type="radio"
                                        name="diabetesControl"
                                        id={`control${num}`}
                                        checked={
                                          step.survey.diabetesControl === num
                                        }
                                        onChange={() =>
                                          handleStepChange(
                                            phase.id,
                                            step.id,
                                            "survey",
                                            {
                                              ...step.survey,
                                              diabetesControl: num,
                                            }
                                          )
                                        }
                                      />
                                      <label
                                        htmlFor={`control${num}`}
                                        className="mb-0"
                                      >
                                        {num === 1
                                          ? "1 - Poor"
                                          : num === 5
                                          ? "5 - Excellent"
                                          : num}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div className="mb-3">
                                <div className="mb-3">
                                  <label className="form-label">
                                    Have you experienced any symptoms?
                                  </label>
                                  <div className="d-flex gap-3 flex-wrap">
                                    {Object.keys(step.survey.symptoms).map(
                                      (symptom, idx) => (
                                        <div
                                          className="d-flex align-items-center gap-1"
                                          key={idx}
                                        >
                                          <input
                                            type="checkbox"
                                            id={`symptom${idx + 1}`}
                                            checked={
                                              step.survey.symptoms[symptom]
                                            }
                                            onChange={() => {
                                              const updatedSymptoms = {
                                                ...step.survey.symptoms,
                                              };
                                              updatedSymptoms[symptom] =
                                                !updatedSymptoms[symptom];
                                              handleStepChange(
                                                phase.id,
                                                step.id,
                                                "survey",
                                                {
                                                  ...step.survey,
                                                  symptoms: updatedSymptoms,
                                                }
                                              );
                                            }}
                                          />
                                          <label
                                            htmlFor={`symptom${idx + 1}`}
                                            className="mb-0"
                                          >
                                            {symptom}
                                          </label>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="mb-3">
                                <label className="form-label">
                                  Additional Comments
                                </label>
                                <textarea
                                  className="form-control"
                                  rows="2"
                                  placeholder="Write your comments here..."
                                  value={step.survey.comments}
                                  onChange={(e) =>
                                    handleStepChange(
                                      phase.id,
                                      step.id,
                                      "survey",
                                      {
                                        ...step.survey,
                                        comments: e.target.value,
                                      }
                                    )
                                  }
                                ></textarea>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}

          {/* Sección de monitoreo */}
          <div className="card mb-4">
            <h5 className="card-header d-flex justify-content-between align-items-center">
              Programming and Monitoring
            </h5>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div>
                    <label className="form-label">Setting Reminders</label>
                    <div className="d-flex">
                      <div className="me-3">
                        <input
                          type="checkbox"
                          id="sms"
                          checked={state.monitoring.reminders.sms}
                          onChange={() => handleReminderToggle("sms")}
                        />
                        <label htmlFor="sms">SMS</label>
                      </div>
                      <div className="me-3">
                        <input
                          type="checkbox"
                          id="email"
                          checked={state.monitoring.reminders.email}
                          onChange={() => handleReminderToggle("email")}
                        />
                        <label htmlFor="email">Email</label>
                      </div>
                      <div className="me-3">
                        <input
                          type="checkbox"
                          id="notificationApp"
                          checked={state.monitoring.reminders.notificationApp}
                          onChange={() =>
                            handleReminderToggle("notificationApp")
                          }
                        />
                        <label htmlFor="notificationApp">
                          Notification App
                        </label>
                      </div>
                      <div>
                        <input
                          type="checkbox"
                          id="call"
                          checked={state.monitoring.reminders.call}
                          onChange={() => handleReminderToggle("call")}
                        />
                        <label htmlFor="call">Call</label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div>
                    <label className="form-label">Alert Recipients</label>
                    <div className="d-flex">
                      <div className="me-3">
                        <input
                          type="checkbox"
                          id="patient"
                          checked={state.monitoring.recipients.patient}
                          onChange={() => handleRecipientToggle("patient")}
                        />
                        <label htmlFor="patient">Patient</label>
                      </div>
                      <div className="me-3">
                        <input
                          type="checkbox"
                          id="doctor"
                          checked={state.monitoring.recipients.doctor}
                          onChange={() => handleRecipientToggle("doctor")}
                        />
                        <label htmlFor="doctor">Treating Doctor</label>
                      </div>
                      <div className="me-3">
                        <input
                          type="checkbox"
                          id="family"
                          checked={state.monitoring.recipients.family}
                          onChange={() => handleRecipientToggle("family")}
                        />
                        <label htmlFor="family">Family</label>
                      </div>
                      <div>
                        <input
                          type="checkbox"
                          id="nursing"
                          checked={state.monitoring.recipients.nursing}
                          onChange={() => handleRecipientToggle("nursing")}
                        />
                        <label htmlFor="nursing">Nursing</label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="mt-3">Compliance Thresholds</p>
              <p>
                Generate alert if compliance is less than
                <input
                  type="number"
                  style={{ width: "55px", marginLeft: "10px" }}
                  value={state.monitoring.complianceThreshold}
                  onChange={(e) =>
                    handleComplianceThresholdChange(
                      Number.parseInt(e.target.value)
                    )
                  }
                />
                <strong>%</strong>
              </p>
              <div className="form-group">
                <label>Actions in Case of Non-compliance</label>
                <select
                  multiple
                  className="rounded form-select"
                  style={{ height: "100px", marginTop: "10px" }}
                  value={state.monitoring.nonComplianceActions}
                  onChange={handleNonComplianceActionsChange}
                >
                  <option value="Phone contact with patient">
                    Phone contact with patient
                  </option>
                  <option value="Reschedule appointment">
                    Reschedule appointment
                  </option>
                  <option value="Notify family member">
                    Notify family member
                  </option>
                  <option value="Home visit">Home visit</option>
                  <option value="Evaluate treatment abandonment">
                    Evaluate treatment abandonment
                  </option>
                </select>
              </div>
            </div>
          </div>

          {/* Sección de recursos */}
          <div className="card mb-4">
            <h5 className="card-header d-flex justify-content-between align-items-center">
              Necessary Resources{" "}
              <button className="btn btn-primary" onClick={handleAddResource}>
                + Add Resource
              </button>
            </h5>
            <div className="table-responsive">
              <table className="table mb-0">
                <thead className="table-secondary text-center">
                  <tr>
                    <th style={{ width: "25%" }}>Name</th>
                    <th style={{ width: "20%" }}>Type</th>
                    <th style={{ width: "25%" }}>Responsible</th>
                    <th style={{ width: "20%" }}>Availability</th>
                    <th style={{ width: "10%" }}>Actions</th>
                  </tr>
                </thead>
                <tbody className="align-middle text-center">
                  {state.resources && state.resources.length > 0 ? (
                    state.resources.map((resource) => (
                      <tr key={resource.id}>
                        <td className="p-3">{resource.name}</td>
                        <td className="p-3">{resource.type}</td>
                        <td className="p-3">{resource.responsible}</td>
                        <td className="p-3">{resource.availability}</td>
                        <td className="p-3">
                          <span
                            className="me-3"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleEditResource(resource)}
                          >
                            ✏️
                          </span>
                          <span
                            style={{ cursor: "pointer" }}
                            onClick={() => handleDeleteResource(resource.id)}
                          >
                            🗑️
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center p-3">
                        No resources added yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Botones de acción */}
          <div className="text-end">
            <button className="btn btn-danger me-2" onClick={handleCancel}>
              Cancel
            </button>
            <button className="btn btn-success me-2" onClick={handleSave}>
              Save Template
            </button>
            <button className="btn btn-primary" onClick={handlePublish}>
              Publish
            </button>
          </div>
        </div>
      </div>

      {/* Modales */}
      <Modal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        title="Save Treatment Template"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => setShowSaveModal(false)}
            >
              Cancel
            </button>
            <button className="btn btn-success" onClick={confirmSave}>
              Save
            </button>
          </>
        }
      >
        <p>Are you sure you want to save this treatment template?</p>
        <p>All changes will be saved.</p>
      </Modal>

      <Modal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        title="Preview Treatment Template"
        size="lg"
      >
        <div className="p-3">
          <h3>{state.metadata.templateName}</h3>
          <p>
            <strong>Protocol:</strong> {state.metadata.protocol}
          </p>
          <p>
            <strong>Version:</strong> {state.metadata.version}
          </p>
          <p>
            <strong>Specialty:</strong> {state.metadata.specialty}
          </p>
          <p>
            <strong>Status:</strong> {state.metadata.status}
          </p>
          <p>
            <strong>Purpose:</strong> {state.metadata.purpose}
          </p>

          <hr />

          <h4>Phases</h4>
          {state.phases.map((phase, index) => (
            <div key={index} className="card mb-3">
              <div className="card-header bg-primary text-white">
                {phase.name} ({phase.startTime})
              </div>
              <div className="card-body">
                <p>
                  <strong>Duration:</strong> {phase.duration.value}{" "}
                  {phase.duration.unit}
                </p>
                <p>
                  <strong>Responsible:</strong> {phase.responsible}
                </p>
                {phase.criteria && (
                  <p>
                    <strong>Criteria:</strong> {phase.criteria}
                  </p>
                )}

                <h5>Steps</h5>
                {phase.steps.map((step, stepIndex) => (
                  <div key={stepIndex} className="mb-3 border-bottom pb-3">
                    <p>
                      <strong>Step {stepIndex + 1}:</strong> {step.name}
                    </p>
                    <p>
                      <strong>Type:</strong> {step.type}
                    </p>
                    {step.description && (
                      <p>
                        <strong>Description:</strong> {step.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Modal>

      <Modal
        isOpen={showAddFieldModal}
        onClose={() => setShowAddFieldModal(false)}
        title="Add Clinical Data Field"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => setShowAddFieldModal(false)}
            >
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleAddCustomField}>
              Add Field
            </button>
          </>
        }
      >
        <div className="mb-3">
          <label className="form-label">Field Name</label>
          <input
            type="text"
            className="form-control"
            value={newFieldData.name}
            onChange={(e) =>
              setNewFieldData({ ...newFieldData, name: e.target.value })
            }
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Field Type</label>
          <select
            className="form-select"
            value={newFieldData.type}
            onChange={(e) =>
              setNewFieldData({ ...newFieldData, type: e.target.value })
            }
          >
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="date">Date</option>
            <option value="select">Select</option>
            <option value="checkbox">Checkbox</option>
          </select>
        </div>
      </Modal>

      <Modal
        isOpen={showEditPhaseModal}
        onClose={() => setShowEditPhaseModal(false)}
        title={selectedPhase ? "Edit Phase" : "Add New Phase"}
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => setShowEditPhaseModal(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={selectedPhase ? saveEditedPhase : handleAddPhase}
            >
              {selectedPhase ? "Save Changes" : "Add Phase"}
            </button>
          </>
        }
      >
        <div className="mb-3">
          <label className="form-label">Phase Name</label>
          <input
            type="text"
            className="form-control"
            value={newPhaseData.name}
            onChange={(e) =>
              setNewPhaseData({ ...newPhaseData, name: e.target.value })
            }
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Start Time</label>
          <input
            type="text"
            className="form-control"
            value={newPhaseData.startTime}
            onChange={(e) =>
              setNewPhaseData({ ...newPhaseData, startTime: e.target.value })
            }
            placeholder="e.g., Immediate Start - Day 0"
          />
        </div>
        <div className="row mb-3">
          <div className="col-md-6">
            <label className="form-label">Duration</label>
            <input
              type="number"
              className="form-control"
              value={newPhaseData.duration.value}
              onChange={(e) =>
                setNewPhaseData({
                  ...newPhaseData,
                  duration: {
                    ...newPhaseData.duration,
                    value: Number.parseInt(e.target.value),
                  },
                })
              }
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Unit</label>
            <select
              className="form-select"
              value={newPhaseData.duration.unit}
              onChange={(e) =>
                setNewPhaseData({
                  ...newPhaseData,
                  duration: { ...newPhaseData.duration, unit: e.target.value },
                })
              }
            >
              <option value="days">Days</option>
              <option value="weeks">Weeks</option>
              <option value="months">Months</option>
            </select>
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label">Responsible</label>
          <select
            className="form-select"
            value={newPhaseData.responsible}
            onChange={(e) =>
              setNewPhaseData({ ...newPhaseData, responsible: e.target.value })
            }
          >
            <option value="">Select responsible</option>
            <option value="treating">Treating doctor</option>
            <option value="endocrinologist">Endocrinologist</option>
            <option value="nurse">Nurse</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Criteria for Finalizing</label>
          <textarea
            className="form-control"
            rows="2"
            value={newPhaseData.criteria}
            onChange={(e) =>
              setNewPhaseData({ ...newPhaseData, criteria: e.target.value })
            }
          ></textarea>
        </div>
      </Modal>

      <Modal
        isOpen={showEditStepModal}
        onClose={() => setShowEditStepModal(false)}
        title={selectedStep ? "Edit Step" : "Add New Step"}
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => setShowEditStepModal(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={selectedStep ? saveEditedStep : saveNewStep}
            >
              {selectedStep ? "Save Changes" : "Add Step"}
            </button>
          </>
        }
      >
        <div className="mb-3">
          <label className="form-label">Step Name</label>
          <input
            type="text"
            className="form-control"
            value={newStepData.name}
            onChange={(e) =>
              setNewStepData({ ...newStepData, name: e.target.value })
            }
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Type of Intervention</label>
          <select
            className="form-select"
            value={newStepData.type}
            onChange={(e) =>
              setNewStepData({ ...newStepData, type: e.target.value })
            }
          >
            <option>Consultation</option>
            <option>Medication</option>
            <option>Test</option>
            <option>Education</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Coordinator</label>
          <select
            className="form-select"
            value={newStepData.coordinator}
            onChange={(e) =>
              setNewStepData({ ...newStepData, coordinator: e.target.value })
            }
          >
            <option value="">Select coordinator</option>
            <option>Doctor and Patient</option>
            <option>Doctor Only</option>
            <option>Patient Only</option>
            <option>Nursing</option>
            <option>Nutritionist</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Relative Time (days)</label>
          <input
            type="number"
            className="form-control"
            value={newStepData.relativeTime}
            onChange={(e) =>
              setNewStepData({
                ...newStepData,
                relativeTime: Number.parseInt(e.target.value),
              })
            }
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Description</label>
          <textarea
            className="form-control"
            rows="2"
            value={newStepData.description}
            onChange={(e) =>
              setNewStepData({ ...newStepData, description: e.target.value })
            }
          ></textarea>
        </div>
      </Modal>

      <Modal
        isOpen={showAddResourceModal}
        onClose={() => setShowAddResourceModal(false)}
        title="Add Resource"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => setShowAddResourceModal(false)}
            >
              Cancel
            </button>
            <button className="btn btn-primary" onClick={saveNewResource}>
              Add Resource
            </button>
          </>
        }
      >
        <div className="mb-3">
          <label className="form-label">Resource Name</label>
          <input
            type="text"
            className="form-control"
            value={newResourceData.name}
            onChange={(e) =>
              setNewResourceData({ ...newResourceData, name: e.target.value })
            }
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Type</label>
          <select
            className="form-select"
            value={newResourceData.type}
            onChange={(e) =>
              setNewResourceData({ ...newResourceData, type: e.target.value })
            }
          >
            <option value="">Select type</option>
            <option value="Equipment">Equipment</option>
            <option value="Room">Room</option>
            <option value="Medical Staff">Medical Staff</option>
            <option value="Medication">Medication</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Responsible</label>
          <input
            type="text"
            className="form-control"
            value={newResourceData.responsible}
            onChange={(e) =>
              setNewResourceData({
                ...newResourceData,
                responsible: e.target.value,
              })
            }
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Availability</label>
          <select
            className="form-select"
            value={newResourceData.availability}
            onChange={(e) =>
              setNewResourceData({
                ...newResourceData,
                availability: e.target.value,
              })
            }
          >
            <option value="">Select availability</option>
            <option value="Continuous">Continuous</option>
            <option value="Scheduled">Scheduled</option>
            <option value="On time">On time</option>
            <option value="Limited">Limited</option>
          </select>
        </div>
      </Modal>

      <Modal
        isOpen={showEditResourceModal}
        onClose={() => setShowEditResourceModal(false)}
        title="Edit Resource"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => setShowEditResourceModal(false)}
            >
              Cancel
            </button>
            <button className="btn btn-primary" onClick={saveEditedResource}>
              Save Changes
            </button>
          </>
        }
      >
        <div className="mb-3">
          <label className="form-label">Resource Name</label>
          <input
            type="text"
            className="form-control"
            value={newResourceData.name}
            onChange={(e) =>
              setNewResourceData({ ...newResourceData, name: e.target.value })
            }
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Type</label>
          <select
            className="form-select"
            value={newResourceData.type}
            onChange={(e) =>
              setNewResourceData({ ...newResourceData, type: e.target.value })
            }
          >
            <option value="">Select type</option>
            <option value="Equipment">Equipment</option>
            <option value="Room">Room</option>
            <option value="Medical Staff">Medical Staff</option>
            <option value="Medication">Medication</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Responsible</label>
          <input
            type="text"
            className="form-control"
            value={newResourceData.responsible}
            onChange={(e) =>
              setNewResourceData({
                ...newResourceData,
                responsible: e.target.value,
              })
            }
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Availability</label>
          <select
            className="form-select"
            value={newResourceData.availability}
            onChange={(e) =>
              setNewResourceData({
                ...newResourceData,
                availability: e.target.value,
              })
            }
          >
            <option value="">Select availability</option>
            <option value="Continuous">Continuous</option>
            <option value="Scheduled">Scheduled</option>
            <option value="On time">On time</option>
            <option value="Limited">Limited</option>
          </select>
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={showDeletePhaseModal}
        onClose={() => setShowDeletePhaseModal(false)}
        onConfirm={confirmDeletePhase}
        title="Delete Phase"
        message="Are you sure you want to delete this phase? This action cannot be undone."
      />

      <ConfirmationModal
        isOpen={showDeleteStepModal}
        onClose={() => setShowDeleteStepModal(false)}
        onConfirm={confirmDeleteStep}
        title="Delete Step"
        message="Are you sure you want to delete this step? This action cannot be undone."
      />

      <ConfirmationModal
        isOpen={showDeleteMedicationModal}
        onClose={() => setShowDeleteMedicationModal(false)}
        onConfirm={confirmDeleteMedication}
        title="Delete Medication"
        message="Are you sure you want to delete this medication? This action cannot be undone."
      />

      <ConfirmationModal
        isOpen={showDeleteResourceModal}
        onClose={() => setShowDeleteResourceModal(false)}
        onConfirm={confirmDeleteResource}
        title="Delete Resource"
        message="Are you sure you want to delete this resource? This action cannot be undone."
      />

      <ConfirmationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={confirmCancel}
        title="Cancel Changes"
        message="Are you sure you want to cancel? All unsaved changes will be lost."
      />

      <ConfirmationModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onConfirm={confirmPublish}
        title="Publish Treatment Template"
        message="Are you sure you want to publish this treatment template? Once published, it will be available for use."
      />
    </main>
  );
}

export default Treatment;
